import "./DistributionListManagement.css";

import React, { useEffect, useState } from "react";
import {
    createNewDistributionList,
    getAllDistributionList,
    updateDistributionList,
} from "../../../services/svt/foundation-service";

import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import IconButton from "../../common/IconButton/IconButton";
import Loader from "../../common/Loader/Loader";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import PlusIcon from "../../../assets/PlusIcon";
import TableComponent from "../../common/TableComponent/TableComponent";
import { formatDate } from "../../../utils/dateFormat";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const DistributionListManagement = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

    const navigate = useNavigate();

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const response = await getAllDistributionList();
            if (response?.data) {
                setGroups(response.data);
            }
        } catch (err) {
            console.error("Error fetching distribution lists:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleOpenModal = () => {
        setFormData({
            title: "",
            description: "",
            status: "Active",
        });
        setIsEditMode(false);
        setEditIndex(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsEditMode(false);
        setEditIndex(null);
    };

    const handleInputChange = (target) => {
        const { name, value } = target;
        toast.dismiss();
        if (name === "title" && value.length > 255) {
            toast.error(t("nameCannotExceedError", { number: 255 }), {
                position: "top-center",
            });
            return;
        }
        if (name === "description" && value.length > 255) {
            toast.error(t("descriptionCannotExceedError", { number: 255 }), {
                position: "top-center",
            });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            [target.name]: target.value,
        }));
    };

    const trimText = (text) => {
        return text.trim().replace(/\s+/g, " ");
    };

    const handleAddOrEditGroup = async () => {
        const { title, description, status } = formData || {};
        const trimmedTitle = trimText(title || "");
        const trimmedDescription = trimText(description || "");

        // Validation
        if (!trimmedTitle) {
            toast.error(t("missingRequiredFieldName"));
            return;
        }
        if (!trimmedDescription) {
            toast.error(t("missingRequiredFieldDescription"));
            return;
        }
        if (trimmedTitle.length > 255) {
            toast.error(t("nameFieldExceedsMaxLengthError", { number: 255 }));
            return;
        }
        if (trimmedDescription.length > 255) {
            toast.error(t("descriptionFieldExceedsMaxLengthError", { number: 255 }));
            return;
        }

        setLoading(true);

        const payload = {
            title: trimmedTitle,
            description: trimmedDescription,
            recipientsEmail: null,
            status: status === "Active",
        };

        const handleResponse = async (response, action) => {
            if (response?.statusCode === 200 || response?.statusCode === 201) {
                await fetchGroups();
                handleCloseModal();
            } else {
                console.error(
                    `Failed to ${action} distribution list:`,
                    response
                );
            }
        };

        try {
            if (isEditMode) {
                const groupId = groups[editIndex]?.id;
                if (!groupId) {
                    console.error("Group ID not found for update");
                    return;
                }
                const response = await updateDistributionList(payload, groupId);
                await handleResponse(response, "update");
            } else {
                const response = await createNewDistributionList(payload);
                await handleResponse(response, "create");
            }
        } catch (err) {
            console.error(
                `Error ${isEditMode ? "updating" : "creating"} distribution list:`,
                err
            );
        } finally {
            setLoading(false);
        }
    };

  const columns = [
    { name: t("name"), field: "title", selector: row => row.title, sortable: true },
    { name: t("description"), field: "description", selector: row => row.description, sortable: true },
    { name: t("addedDate"), field: "createdDate", selector: row => row.createdDate, cell: row => formatDate(row.createdDate), sortable: true, },
    { name: t("addedBy"), field: "createdBy", selector: row => row.createdBy, sortable: true, },
    { name: t("status"), field: 'status', selector: row => row.isActive, sortable: true, },
  ];

    return loading ? (
        <Loader />
    ) : (
        <div className="group-management-container">
            {/* <div className="group-management-header">
        <IconButton
          text="Distribution List"
          onClick={handleOpenModal}
          style={{
            backgroundColor: "#F0F0F0",
            color: "#454647",
            border: "1px solid #B3B2B4",
          }}
          icon={<PlusIcon />}
        />
      </div> */}

      <div className="surveyHeader">
        <h2 className="surveyTitle">{t("listOfDistributions")}</h2>
        <IconButton
          text={t("distributionList")}
          onClick={handleOpenModal}
          style={{
            backgroundColor: "#F0F0F0",
            color: "#454647",
            border: "1px solid #B3B2B4",
          }}
          icon={<PlusIcon />}
        />
      </div>
      <hr></hr>

            <div style={{ marginTop: "1rem" }}>
                <DataTableComponent
                    columns={columns}
                    data={groups}
                    tableName="GroupManagement"
                    pageCount={5}
                    onEditDetails={(row, index) => {
                        setFormData({
                            title: row.title, // Corrected from name -> title
                            description: row.description,
                            status: row.isActive ? "Active" : "De-Active",
                        });
                        setIsEditMode(true);
                        setEditIndex(index);
                        setIsModalOpen(true);
                    }}
                    handleView={(row) => {
                        navigate(
                            `/admin/foundation/distribution-list/group-details`,
                            {
                                state: { group: row },
                            }
                        );
                    }}
                />
            </div>

      <ModalComponent
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalTitle={isEditMode ? t("updateDistributionList") : t("distributionList")}
        fieldsData={[
          { fieldName: t("name"), name: "title", type: "input", required: true },
          { fieldName: t("description"), name: "description", type: "textarea", required: true },
          {
            fieldName: t("status"),
            name: "status",
            type: "dropdown",
            options: [
              { label: t("active"), value: "Active" },
              { label: t("deActive"), value: "De-Active" },
            ],
          },
        ]}
        formData={formData}
        onChangeInput={handleInputChange}
        buttons={[
          <button key="add" className="saveButton" onClick={handleAddOrEditGroup}>
            {isEditMode ? t("update") : t("add")}
          </button>,
          <button key="cancel" className="cancelButton" onClick={handleCloseModal}>
            {t("cancel")}
          </button>,
        ]}
      />
    </div>
  );
};

export default DistributionListManagement;
